import dayjs from "dayjs";
import { useTenant } from "hooks/settings";
import { useDayjsDateStringParamState } from "hooks/useDayjsDateStringParamState";
import { useIsBehindFeatureFlag } from "hooks/useFeatureFlag";
import { useLocalDatetime } from "hooks/useLocalDateTime";
import { createContext, ReactNode, use, useMemo } from "react";
import {
  StrategyDashboardConsumptionProjectionsData,
  useGetStrategyDashboardConsumptionProjectionQuery,
} from "src/store/api/generatedApi";

interface ConsumptionTabProjectionContextType {
  selectedDate: dayjs.Dayjs;
  setSelectedDate: (date: dayjs.Dayjs) => void;
  data: StrategyDashboardConsumptionProjectionsData | undefined;
  isFetching: boolean;
  isError: boolean;
  isLoading: boolean;
}

const ConsumptionTabProjectionContext = createContext<
  ConsumptionTabProjectionContextType | undefined
>(undefined);

interface ConsumptionTabProjectionProviderProps {
  children: ReactNode;
}

export const ConsumptionTabProjectionProvider = ({
  children,
}: ConsumptionTabProjectionProviderProps) => {
  const consumptionProjectionEnabled = useIsBehindFeatureFlag(
    "strategy_dashboard_consumption_projection"
  );
  const { localTimezone } = useLocalDatetime();
  const [selectedDate, setSelectedDate] = useDayjsDateStringParamState(
    "date",
    dayjs().tz(localTimezone),
    localTimezone
  );
  const {
    data: queryData,
    isError,
    isLoading,
    isFetching,
  } = useGetStrategyDashboardConsumptionProjectionQuery(
    {
      tenantName: useTenant(),
      on: selectedDate.toISOString(),
    },
    { skip: !consumptionProjectionEnabled }
  );
  const data = useMemo(
    () => (consumptionProjectionEnabled ? queryData : { values: [] }),
    [consumptionProjectionEnabled, queryData]
  );

  const value = useMemo(
    () => ({
      selectedDate,
      setSelectedDate,
      data: data,
      isFetching,
      isError,
      isLoading,
    }),
    [selectedDate, setSelectedDate, data, isFetching, isError, isLoading]
  );

  return (
    <ConsumptionTabProjectionContext value={value}>
      {children}
    </ConsumptionTabProjectionContext>
  );
};

export const useConsumptionTabProjection = () => {
  const context = use(ConsumptionTabProjectionContext);
  if (context === undefined) {
    throw new Error(
      "useConsumptionTabProjection must be used within a ConsumptionTabProjectionProvider"
    );
  }
  return context;
};
